import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

export default function RenewalAlerts({ branchId }) {
  useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewingId, setRenewingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const sevenDaysLater = new Date(today);
      sevenDaysLater?.setDate(today?.getDate() + 7);
      const branchParam = branchId ? { branch_id: branchId } : {};

      const { data: members } = await api.get('/members', { params: { limit: 2000, ...branchParam } });

      // Filter members with active packages expiring within 7 days (or already expired)
      const filtered = (members || [])?.filter(m => {
        if (!m?.package_end_date) return false;
        return new Date(m?.package_end_date) <= sevenDaysLater;
      });

      // Sort by end_date ascending
      filtered?.sort((a, b) => new Date(a?.package_end_date) - new Date(b?.package_end_date));

      const enriched = filtered?.map(m => {
        const endDate = new Date(m?.package_end_date);
        const diffMs = endDate - today;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return {
          id: m?.id,
          member_id: m?.id,
          end_date: m?.package_end_date,
          start_date: m?.package_start_date,
          package_name: m?.package_name,
          price: m?.package_price || 0,
          memberName: `${m?.first_name || ""} ${m?.last_name || ""}`?.trim(),
          memberEmail: m?.email || "",
          memberStatus: m?.member_status || "",
          diffDays,
        };
      });

      setAlerts(enriched);
    } catch (err) {
      console.error("Error fetching renewal alerts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [branchId]);

  const handleRenew = async (alert) => {
    try {
      setRenewingId(alert?.id);
      const today = new Date()?.toISOString()?.split("T")?.[0];
      // Calculate new end date: extend from today by same duration as original package
      const origStart = new Date(alert?.start_date || today);
      const origEnd = new Date(alert?.end_date);
      const durationMs = origEnd - origStart;
      const durationDays = Math.max(30, Math.round(durationMs / (1000 * 60 * 60 * 24)));
      const newEnd = new Date(today);
      newEnd?.setDate(newEnd?.getDate() + durationDays);
      const newEndStr = newEnd?.toISOString()?.split("T")?.[0];

      // POST /members/:id/packages auto-deactivates old package and creates new one
      await api.post(`/members/${alert?.member_id}/packages`, {
        package_name: alert?.package_name,
        package_type: "monthly",
        price: alert?.price,
        start_date: today,
        end_date: newEndStr,
      });

      showToast(`Renewed ${alert?.memberName}'s membership until ${new Date(newEndStr)?.toLocaleDateString()}`);
      await fetchAlerts();
    } catch (err) {
      console.error("Error renewing membership:", err);
      showToast("Failed to renew membership. Please try again.", "error");
    } finally {
      setRenewingId(null);
    }
  };

  const getDaysLabel = (diffDays) => {
    if (diffDays < 0) return `Expired ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""} ago`;
    if (diffDays === 0) return "Expires today";
    return `${diffDays} day${diffDays !== 1 ? "s" : ""} left`;
  };

  const getDaysColor = (diffDays) => {
    if (diffDays < 0) return "var(--color-error)";
    if (diffDays <= 2) return "var(--color-error)";
    if (diffDays <= 5) return "var(--color-warning)";
    return "#f59e0b";
  };

  const getDaysBadgeBg = (diffDays) => {
    if (diffDays < 0) return "bg-[var(--color-error)]/10 text-[var(--color-error)] border-[var(--color-error)]/20";
    if (diffDays <= 2) return "bg-[var(--color-error)]/10 text-[var(--color-error)] border-[var(--color-error)]/20";
    if (diffDays <= 5) return "bg-[var(--color-warning)]/10 text-[var(--color-warning)] border-[var(--color-warning)]/20";
    return "bg-amber-50 text-amber-600 border-amber-200";
  };

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium font-[var(--font-caption)] transition-all ${
            toast?.type === "error" ?"bg-[var(--color-error)] text-white" :"bg-[var(--color-success)] text-white"
          }`}
        >
          <Icon
            name={toast?.type === "error" ? "AlertCircle" : "CheckCircle"}
            size={15}
            color="white"
          />
          {toast?.message}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[var(--color-error)]/10 flex items-center justify-center">
            <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              Renewal Alerts
            </h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              {loading
                ? "Loading..."
                : `${alerts?.length} member${alerts?.length !== 1 ? "s" : ""} need attention`}
            </p>
          </div>
        </div>
        <button
          onClick={fetchAlerts}
          className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors"
          title="Refresh"
        >
          <Icon name="RefreshCw" size={13} color="var(--color-muted-foreground)" />
        </button>
      </div>
      {/* Content */}
      {loading ? (
        <div className="p-6 flex items-center justify-center gap-2 text-sm text-[var(--color-muted-foreground)]">
          <Icon name="Loader2" size={15} color="var(--color-muted-foreground)" className="animate-spin" />
          Loading renewal alerts...
        </div>
      ) : alerts?.length === 0 ? (
        <div className="p-8 flex flex-col items-center justify-center gap-2 text-center">
          <div className="w-10 h-10 rounded-full bg-[var(--color-success)]/10 flex items-center justify-center">
            <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
          </div>
          <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)]">
            All memberships are up to date
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            No renewals due in the next 7 days
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[var(--color-border)]">
          {alerts?.map((alert) => (
            <div
              key={alert?.id}
              className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-[var(--color-muted)]/20 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white font-[var(--font-heading)]"
                  style={{ backgroundColor: getDaysColor(alert?.diffDays) }}
                >
                  {alert?.memberName?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold font-[var(--font-body)] text-[var(--color-foreground)] truncate">
                    {alert?.memberName || "Unknown Member"}
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] truncate">
                    {alert?.package_name || "—"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                    Renewal Date
                  </p>
                  <p className="text-xs font-semibold font-[var(--font-data)] text-[var(--color-foreground)]">
                    {alert?.end_date
                      ? new Date(alert?.end_date)?.toLocaleDateString()
                      : "—"}
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold font-[var(--font-caption)] border whitespace-nowrap ${getDaysBadgeBg(alert?.diffDays)}`}
                >
                  {getDaysLabel(alert?.diffDays)}
                </span>
                <button
                  onClick={() => handleRenew(alert)}
                  disabled={renewingId === alert?.id}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {renewingId === alert?.id ? (
                    <>
                      <Icon name="Loader2" size={11} color="white" className="animate-spin" />
                      Renewing...
                    </>
                  ) : (
                    <>
                      <Icon name="RefreshCcw" size={11} color="white" />
                      Renew
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
